import type { DXBlueprintContent, ContentCreateRequest } from '../../../services/contentService';
import type { ContentDetailData } from './contentDetailTypes';

/**
 * Transform API content data to detail form data format
 */
export const transformApiContentToDetailData = (apiContent: DXBlueprintContent): ContentDetailData => {
  return {
    overview: {
      dxNo: apiContent.dx_number || '',
      titleTh: apiContent.title_th || '',
      titleEn: apiContent.title_en || '',
      shortDescTh: apiContent.short_description_th || '',
      shortDescEn: apiContent.short_description_en || '',
    },
    problemStatement: {
      statementTh: apiContent.problem_statement_th || '',
      statementEn: apiContent.problem_statement_en || '',
    },
    painPoint: apiContent.pain_point_items?.map(item => ({
      titleTh: item.title_th || '',
      titleEn: item.title_en || '',
      descTh: item.description_th || '',
      descEn: item.description_en || '',
    })) || [{ titleTh: '', titleEn: '', descTh: '', descEn: '' }],
    gapToImprove: apiContent.gap_to_improve_items?.map(item => ({
      titleTh: item.title_th || '',
      titleEn: item.title_en || '',
      descTh: item.description_th || '',
      descEn: item.description_en || '',
    })) || [{ titleTh: '', titleEn: '', descTh: '', descEn: '' }],
    strategicObjective: apiContent.strategic_objectives_th && apiContent.strategic_objectives_en ? [{
      objectiveTh: apiContent.strategic_objectives_th,
      objectiveEn: apiContent.strategic_objectives_en,
    }] : [{ objectiveTh: '', objectiveEn: '' }],
    businessImpact: apiContent.business_impacts_th && apiContent.business_impacts_en ? [{
      impactTh: apiContent.business_impacts_th,
      impactEn: apiContent.business_impacts_en,
    }] : [{ impactTh: '', impactEn: '' }],
    endGoals: apiContent.end_goals_th && apiContent.end_goals_en ? [{
      goalTh: apiContent.end_goals_th,
      goalEn: apiContent.end_goals_en,
    }] : [{ goalTh: '', goalEn: '' }],
    pillar: apiContent.key_capabilities_pillars_th && apiContent.key_capabilities_pillars_en ? [{
      pillarTh: apiContent.key_capabilities_pillars_th,
      pillarEn: apiContent.key_capabilities_pillars_en,
    }] : [{ pillarTh: '', pillarEn: '' }],
    useCases: apiContent.use_case_items?.map(item => ({
      nameTh: item.name_th || '',
      nameEn: item.name_en || '',
      details: item.use_case_details?.map(detail => ({
        currentStepTh: detail.current_step_stones_th || '',
        currentStepEn: detail.current_step_stones_en || '',
        nextMoveTh: detail.next_move_th || '',
        nextMoveEn: detail.next_move_en || '',
        potentialValueTh: detail.potential_value_creations_th || '',
        potentialValueEn: detail.potential_value_creations_en || '',
      })) || [{
        currentStepTh: item.description_th || '',
        currentStepEn: item.description_en || '',
        nextMoveTh: '',
        nextMoveEn: '',
        potentialValueTh: '',
        potentialValueEn: '',
      }],
    })) || [{
      nameTh: '',
      nameEn: '',
      details: [{
        currentStepTh: '',
        currentStepEn: '',
        nextMoveTh: '',
        nextMoveEn: '',
        potentialValueTh: '',
        potentialValueEn: '',
      }],
    }],
    technology: {
      titleTh: apiContent.technologies_enablers_items?.[0]?.title_th || '',
      titleEn: apiContent.technologies_enablers_items?.[0]?.title_en || '',
    },
    keyChallenges: apiContent.key_challenges_items?.map(item => ({
      challengeTh: item.title_th || '',
      challengeEn: item.title_en || '',
    })) || [{ challengeTh: '', challengeEn: '' }],
    technologyImage: {
      images: apiContent.technologies_enablers_images || [], // Now uses asset IDs
    },
    timeline: apiContent.timeline_items?.map(item => ({
      phaseNameTh: item.phase_th || '',
      phaseNameEn: item.phase_en || '',
      durationTh: item.timeline_th || '',
      durationEn: item.timeline_en || '',
      status: (item.status === 'in_progress' ? 'inProgress' : item.status === 'published' ? 'published' : 'inProgress') as 'inProgress' | 'published',
    })) || [{ phaseNameTh: '', phaseNameEn: '', durationTh: '', durationEn: '', status: 'inProgress' as const }],
    permission: {
      owner: apiContent.content_owner || '',
      member: apiContent.content_members || [],
    },
  };
};

/**
 * Check if a string is empty or contains only whitespace
 */
const isEmpty = (value: string | undefined | null): boolean => {
  return !value || value.trim() === '';
};

/**
 * Check if an object has all empty string values
 */
const isObjectEmpty = (obj: Record<string, any>): boolean => {
  return Object.values(obj).every(value => isEmpty(value));
};

/**
 * Transform detail form data to API content format
 */
export const transformDetailDataToApiContent = (detailData: ContentDetailData): ContentCreateRequest => {
  // Filter out empty pain point items
  const filteredPainPointItems = detailData.painPoint
    .filter(item => !isObjectEmpty({
      title_th: item.titleTh,
      title_en: item.titleEn,
      description_th: item.descTh,
      description_en: item.descEn,
    }));

  // Filter out empty gap to improve items
  const filteredGapToImproveItems = detailData.gapToImprove
    .filter(item => !isObjectEmpty({
      title_th: item.titleTh,
      title_en: item.titleEn,
      description_th: item.descTh,
      description_en: item.descEn,
    }));

  // Filter out empty use case items
  const filteredUseCaseItems = detailData.useCases
    .filter(item => {
      // Check if use case name is filled
      const hasName = !isEmpty(item.nameTh) || !isEmpty(item.nameEn);
      
      // Check if at least one detail is filled
      const hasDetails = item.details?.some(detail => 
        !isObjectEmpty({
          current_step_stones_th: detail.currentStepTh,
          current_step_stones_en: detail.currentStepEn,
          next_move_th: detail.nextMoveTh,
          next_move_en: detail.nextMoveEn,
          potential_value_creations_th: detail.potentialValueTh,
          potential_value_creations_en: detail.potentialValueEn,
        })
      );
      
      return hasName || hasDetails;
    })
    .map(item => ({
      name_th: item.nameTh,
      name_en: item.nameEn,
      description_th: item.details?.[0]?.currentStepTh, // Keep for backward compatibility
      description_en: item.details?.[0]?.currentStepEn, // Keep for backward compatibility
      use_case_details: item.details?.filter(detail => 
        !isObjectEmpty({
          current_step_stones_th: detail.currentStepTh,
          current_step_stones_en: detail.currentStepEn,
          next_move_th: detail.nextMoveTh,
          next_move_en: detail.nextMoveEn,
          potential_value_creations_th: detail.potentialValueTh,
          potential_value_creations_en: detail.potentialValueEn,
        })
      ).map(detail => ({
        current_step_stones_th: detail.currentStepTh,
        current_step_stones_en: detail.currentStepEn,
        next_move_th: detail.nextMoveTh,
        next_move_en: detail.nextMoveEn,
        potential_value_creations_th: detail.potentialValueTh,
        potential_value_creations_en: detail.potentialValueEn,
      })) || [],
    }));

  // Filter out empty timeline items
  const filteredTimelineItems = detailData.timeline
    .filter(item => !isObjectEmpty({
      phase_th: item.phaseNameTh,
      phase_en: item.phaseNameEn,
      timeline_th: item.durationTh,
      timeline_en: item.durationEn,
    }));

  // Filter out empty key challenges items
  const filteredKeyChallengesItems = detailData.keyChallenges
    .filter(item => !isObjectEmpty({
      title_th: item.challengeTh,
      title_en: item.challengeEn,
    }));

  // Check if technology has content
  const hasTechnologyContent = !isObjectEmpty({
    title_th: detailData.technology.titleTh,
    title_en: detailData.technology.titleEn,
  });

  return {
    dx_number: detailData.overview.dxNo,
    title_th: detailData.overview.titleTh,
    title_en: detailData.overview.titleEn,
    short_description_th: detailData.overview.shortDescTh,
    short_description_en: detailData.overview.shortDescEn,
    problem_statement_th: detailData.problemStatement.statementTh,
    problem_statement_en: detailData.problemStatement.statementEn,
    pain_point_items: filteredPainPointItems.map(item => ({
      title_th: item.titleTh,
      title_en: item.titleEn,
      description_th: item.descTh,
      description_en: item.descEn,
    })),
    gap_to_improve_items: filteredGapToImproveItems.map(item => ({
      title_th: item.titleTh,
      title_en: item.titleEn,
      description_th: item.descTh,
      description_en: item.descEn,
    })),
    strategic_objectives_th: detailData.strategicObjective[0]?.objectiveTh,
    strategic_objectives_en: detailData.strategicObjective[0]?.objectiveEn,
    business_impacts_th: detailData.businessImpact[0]?.impactTh,
    business_impacts_en: detailData.businessImpact[0]?.impactEn,
    end_goals_th: detailData.endGoals[0]?.goalTh,
    end_goals_en: detailData.endGoals[0]?.goalEn,
    key_capabilities_pillars_th: detailData.pillar[0]?.pillarTh,
    key_capabilities_pillars_en: detailData.pillar[0]?.pillarEn,
    use_case_items: filteredUseCaseItems,
    technologies_enablers_items: hasTechnologyContent ? [{
      title_th: detailData.technology.titleTh,
      title_en: detailData.technology.titleEn,
      description_th: '',
      description_en: '',
    }] : [],
    technologies_enablers_images: detailData.technologyImage.images, // Asset IDs for images
    timeline_items: filteredTimelineItems.map(item => ({
      phase_th: item.phaseNameTh,
      phase_en: item.phaseNameEn,
      timeline_th: item.durationTh,
      timeline_en: item.durationEn,
      status: item.status === 'inProgress' ? 'in_progress' : item.status === 'published' ? 'published' : 'in_progress',
    })),
    key_challenges_items: filteredKeyChallengesItems.map(item => ({
      title_th: item.challengeTh,
      title_en: item.challengeEn,
      description_th: '',
      description_en: '',
    })),
    content_owner: detailData.permission.owner,
    content_members: detailData.permission.member || [],
  };
};